import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import { Phone, MapPin, CheckCircle2, ArrowRight, Key, Building2, Clock, Shield, Paintbrush, Hammer, Layers, Wrench, Droplets, Zap } from 'lucide-react';
import { companyInfo } from '../data/mock';

const VoidPropertyWorks = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', properties: '', message: '', honeypot: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          properties_managed: formData.properties,
          job_description: `[Void Property Works Enquiry] ${formData.message}`,
          trade_type: 'void',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/void-property-works'
        }),
      });
      if (response.ok) { setIsSubmitted(true); setFormData({ name: '', email: '', phone: '', properties: '', message: '', honeypot: '' }); }
      else { setSubmitError(true); }
    } catch { setSubmitError(true); }
    finally { setIsSubmitting(false); }
  };

  const typicalWorks = [
    { icon: Wrench, title: 'Inspection & Repair List', desc: 'Systematic check and repair of all issues identified' },
    { icon: Layers, title: 'Plaster Repairs', desc: 'Filling holes, repairing damage, making good walls and ceilings' },
    { icon: Paintbrush, title: 'Decorating', desc: 'Full or partial redecoration to refresh the property' },
    { icon: Hammer, title: 'Door & Joinery Repairs', desc: 'Fixing doors, skirting, architrave and general woodwork' },
    { icon: Key, title: 'Lock Changes', desc: 'Security — changing locks between tenancies' },
    { icon: Droplets, title: 'Plumbing Repairs', desc: 'Fixing leaks, taps, toilets and general plumbing issues' },
    { icon: Zap, title: 'Electrical Repairs', desc: 'Socket repairs, light fittings, minor electrical works' },
    { icon: Building2, title: 'General Maintenance', desc: 'Sealant renewal, fixture repairs, general making good' }
  ];

  const benefits = [
    'Single point of contact for all trades',
    'Multi-trade capability — one company handles everything',
    'Fast turnaround to minimise void periods',
    'Clear communication and progress updates',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Void Property Works for Landlords & Letting Agents | Expert Property Maintenance</title>
        <meta name="description" content="Void property works in Manchester for landlords and letting agents. Complete turnaround between tenancies — repairs, decorating, plumbing, electrical. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/void-property-works" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Key className="w-4 h-4" />
              Void Property Works
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Void Property Works
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Complete void turnaround services for landlords and letting agents. When a tenant leaves, 
              we handle all the repairs, maintenance and refurbishment needed before the next let.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">Get a Void Works Quote<ArrowRight className="w-5 h-5 ml-2" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What are Void Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">What Are Void Property Works?</h2>
              <p className="text-lg text-brand-slate mb-6">
                When a tenancy ends, properties often need multiple repairs and improvements before they're 
                ready to let again. Damage repairs, decorating, plumbing fixes, door adjustments — the list 
                can include several different trades.
              </p>
              <p className="text-brand-slate mb-6">
                Void property works means handling this entire list through one contractor. Instead of 
                coordinating a painter, a plasterer, a handyman and a plumber, you give us the repair list 
                and we handle everything.
              </p>
              <p className="text-brand-slate mb-8">
                This approach saves you time, reduces coordination headaches, and typically means faster 
                completion — getting your property earning again sooner.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-brand-navy text-white rounded-2xl p-8">
                <Clock className="w-12 h-12 text-brand-yellow mb-4" />
                <h3 className="text-xl font-bold mb-4">Minimise Void Periods</h3>
                <p className="text-slate-300 mb-4">
                  Every day a property sits empty costs money. Fast, efficient void works mean your 
                  property is ready for viewings and lettings sooner.
                </p>
                <p className="text-slate-400 text-sm">
                  We understand the urgency and prioritise void properties accordingly.
                </p>
              </div>
              <div className="bg-brand-grey border border-brand-yellow/30 rounded-2xl p-6">
                <h4 className="font-semibold text-brand-navy mb-4">Important Note</h4>
                <p className="text-sm text-brand-slate">
                  <strong>We are property maintenance contractors — not a cleaning company.</strong> Void 
                  works focuses on repairs, maintenance and refurbishment. Professional cleaning should be 
                  arranged separately if required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Works */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">Typical Void Works</h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Common maintenance and repairs included in void property turnarounds.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {typicalWorks.map((work) => (
              <Card key={work.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-brand-grey rounded-lg flex items-center justify-center mb-4">
                    <work.icon className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{work.title}</h3>
                  <p className="text-brand-slate text-sm">{work.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For Landlords & Agents */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-navy rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Building2 className="w-4 h-4" />
                  For Property Professionals
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">Landlords & Letting Agents</h2>
                <p className="text-slate-300 mb-6">
                  Whether you're a landlord managing your own properties or a letting agent handling maintenance 
                  for clients, void turnarounds are a critical time. Speed and quality both matter — the property 
                  needs to be right for viewings, and delays cost money.
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  Expert Property Maintenance provides the multi-trade capability to handle void works efficiently, 
                  with clear communication throughout the process.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                    <a href="/landlord-services">Landlord Services<ArrowRight className="w-5 h-5 ml-2" /></a>
                  </Button>
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-brand-navy/90" asChild>
                    <a href="/letting-agent-maintenance">Letting Agent Services</a>
                  </Button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Single Contact</h4>
                  <p className="text-sm text-slate-400">One phone number for all void works — no chasing multiple contractors</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Multi-Trade Capability</h4>
                  <p className="text-sm text-slate-400">Carpentry, decorating, plastering, plumbing, electrical — handled together</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Fast Turnaround</h4>
                  <p className="text-sm text-slate-400">We prioritise void properties to minimise your empty periods</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Photo Updates</h4>
                  <p className="text-sm text-slate-400">Progress photos so you can see work being completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/end-of-tenancy" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">End of Tenancy Repairs</h4>
              <p className="text-sm text-brand-slate">Repair-focused turnarounds</p>
            </a>
            <a href="/painting-decorating" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Painting & Decorating</h4>
              <p className="text-sm text-brand-slate">Refresh tired properties</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Major renovations</p>
            </a>
            <a href="/letting-agent-maintenance" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Letting Agent Services</h4>
              <p className="text-sm text-brand-slate">Ongoing maintenance support</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Void Works Quote</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about the property and what needs doing. We will provide a quote and discuss timescales.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><Phone className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">{companyInfo.phone}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><MapPin className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-slate-800 rounded-xl">
                <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
                <div className="text-white font-medium">{companyInfo.insurance.publicLiability}</div>
                <div className="text-white font-medium">{companyInfo.guarantee}</div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch to discuss your void property.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" /></div>
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label><Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Property Address/Postcode</label><Input value={formData.properties} onChange={(e) => setFormData({...formData, properties: e.target.value})} placeholder="Property postcode" className="h-12" /></div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">What works are needed? *</label><Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="List the repairs and works needed, or let us know if you need an inspection..." rows={4} /></div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send Enquiry'}</Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/letting-agent-maintenance" className="hover:text-brand-yellow">Letting Agents</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default VoidPropertyWorks;
